import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route org-level KPI window anchors through clock.
import { clock } from '@/utils/clock';
import { useAuth } from '@/contexts/AuthContext';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { useNetworkSignals } from '@/hooks/useNetworkSignals';
import { useNetworkLearning, type LearningSummaryRow } from '@/hooks/useNetworkLearning';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { applyLearningToRecommendations, type RecommendationWithLearning } from '@/lib/enterprise/learningWeights';
import { applyOutcomeAdjustments, computeOutcomeAdjustment, type RecommendationWithOutcomes } from '@/lib/enterprise/outcomeAdjustments';
import {
  detectCompositePatterns,
  applyPatternAdjustments,
  getPatternBadgeLabel,
  getConfidenceBadgeClass,
  type CompositePattern,
  type RecommendationWithPatterns,
} from '@/lib/enterprise/compositePatterns';
import {
  getOutcomeBadge,
  getOutcomeContext,
  deriveOutcomeInsights,
  type OutcomePattern,
  type OutcomeInsight,
} from '@/lib/enterprise/outcomePatterns';
import { calculateOBLI } from '@/utils/obliCalculation';
import { StatCard } from '@/components/dashboard/StatCard';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';
import { format } from 'date-fns';
import { generateGuidedActions, type GuidedAction } from '@/lib/enterprise/guidedActions';
import { getPlaybook, type Playbook, type PlaybookStep } from '@/lib/enterprise/operationalPlaybooks';
import {
  AlertTriangle, TrendingDown, Clock, Activity, ArrowRight,
  Lightbulb, Eye, Scale, Store, Zap, CheckCircle, Brain,
  ChevronDown, ChevronRight, RefreshCw, Target, Compass, BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
} from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

// ── Types ──────────────────────────────────────────────

type DateRange = 'today' | 'yesterday' | '7d';

interface PerShop {
  shop_id: string;
  shop_name: string;
  address: string | null;
  active_tickets: number;
  completed_in_range: number;
  booked_minutes: number;
  waiter_count: number;
  total_techs: number;
  total_capacity: number;
}

interface DashboardMetrics {
  total_shops: number;
  active_tickets: number;
  completed_in_range: number;
  vehicles_in_shop: number;
  estimated_revenue: number | null;
  total_booked_minutes: number;
  total_capacity_minutes: number;
  per_shop: PerShop[];
}

type RecommendationType =
  | 'OVERLOAD_ATTENTION'
  | 'UNDERUTILIZATION'
  | 'COMPLETION_WEAKNESS'
  | 'WAITER_PRESSURE'
  | 'CAPACITY_IMBALANCE';

interface Recommendation {
  type: RecommendationType;
  scope_level: 'shop' | 'scope';
  shop_id?: string;
  shop_name?: string;
  title: string;
  summary: string;
  reasons: string[];
  priority_score: number;
  severity: 'high' | 'medium' | 'low';
}

type EnrichedRecommendation = Recommendation & RecommendationWithLearning & RecommendationWithPatterns & RecommendationWithOutcomes;

// ── Helpers ────────────────────────────────────────────

// IV-D-22 R73 FIX-3: see OrgIntelligence.tsx for the same fix — UTC-sliced
// dates returned "tomorrow" for PST users after 4 PM local, so the Learning
// Summary and recommendation filters pulled data for a day that hadn't
// happened yet. Use browser-local calendar date.
function getDateRange(range: DateRange): { start: string; end: string } {
  const today = clock.now();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
  if (range === 'yesterday') {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { start: fmt(y), end: fmt(y) };
  }
  if (range === '7d') {
    const w = new Date(today); w.setDate(w.getDate() - 6);
    return { start: fmt(w), end: fmt(today) };
  }
  return { start: fmt(today), end: fmt(today) };
}

function getPriorDateRange(range: DateRange): { start: string; end: string } {
  const today = clock.now();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
  if (range === 'today') {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { start: fmt(y), end: fmt(y) };
  }
  if (range === 'yesterday') {
    const d = new Date(today); d.setDate(d.getDate() - 2);
    return { start: fmt(d), end: fmt(d) };
  }
  const priorEnd = new Date(today); priorEnd.setDate(priorEnd.getDate() - 7);
  const priorStart = new Date(priorEnd); priorStart.setDate(priorStart.getDate() - 6);
  return { start: fmt(priorStart), end: fmt(priorEnd) };
}

function shopOBLI(shop: PerShop) {
  return calculateOBLI({
    totalCapacity: shop.total_capacity || 0,
    bookedMinutes: shop.booked_minutes,
    ticketCount: shop.active_tickets,
    waiterCount: shop.waiter_count,
    specialtyJobCount: 0,
    workingTechCount: shop.total_techs,
    totalTechCount: shop.total_techs,
    isShopOpen: true,
  });
}

// ── Recommendation Engine ──────────────────────────────

function generateRecommendations(
  currentShops: PerShop[],
  priorShops: PerShop[],
): Recommendation[] {
  const recs: Recommendation[] = [];
  const priorMap = new Map<string, PerShop>();
  priorShops.forEach((s) => priorMap.set(s.shop_id, s));

  const enriched = currentShops.map((s) => {
    const obli = shopOBLI(s);
    const utilization = s.total_capacity > 0
      ? Math.round((s.booked_minutes / s.total_capacity) * 100) : 0;
    const waiterRatio = s.active_tickets > 0
      ? s.waiter_count / s.active_tickets : 0;
    const prior = priorMap.get(s.shop_id);
    const priorObli = prior ? shopOBLI(prior).score : null;
    const trendingUp = priorObli !== null && obli.score > priorObli + 5;
    return { ...s, obli, utilization, waiterRatio, prior, priorObli, trendingUp };
  });

  let hasOverloaded = false;
  let hasUnderutilized = false;

  enriched.forEach((s) => {
    const isOverloaded = s.obli.score > 85 || s.utilization > 90;
    const isTrendOverload = s.trendingUp && s.waiterRatio > 0.4;
    if (isOverloaded || isTrendOverload) {
      hasOverloaded = true;
      const reasons: string[] = [];
      if (s.obli.score > 85) reasons.push(`OBLI is ${s.obli.score}`);
      if (s.utilization > 90) reasons.push(`Utilization at ${s.utilization}%`);
      if (s.trendingUp) reasons.push('OBLI trending up vs prior period');
      if (s.waiterRatio > 0.4) reasons.push(`Waiter ratio ${Math.round(s.waiterRatio * 100)}%`);
      recs.push({
        type: 'OVERLOAD_ATTENTION',
        scope_level: 'shop',
        shop_id: s.shop_id,
        shop_name: s.shop_name,
        title: `${s.shop_name} needs attention`,
        summary: `OBLI is elevated, utilization is high, and workload signals suggest this shop may be at or over capacity. Consider reviewing active workload, waiter mix, and technician capacity inside this shop.`,
        reasons,
        priority_score: Math.min(100, s.obli.score),
        severity: 'high',
      });
    }

    if (s.obli.score < 20 && s.utilization < 30) {
      hasUnderutilized = true;
      recs.push({
        type: 'UNDERUTILIZATION',
        scope_level: 'shop',
        shop_id: s.shop_id,
        shop_name: s.shop_name,
        title: `${s.shop_name} appears underutilized`,
        summary: `Load and technician utilization are both low relative to your current scope. This may indicate available capacity or weaker inbound volume.`,
        reasons: [
          `OBLI is ${s.obli.score}`,
          `Utilization at ${s.utilization}%`,
        ],
        priority_score: 100 - s.obli.score,
        severity: 'low',
      });
    }

    if (s.prior && s.prior.completed_in_range > 0) {
      if (s.completed_in_range < s.prior.completed_in_range * 0.75) {
        const declinePct = Math.round(
          ((s.prior.completed_in_range - s.completed_in_range) / s.prior.completed_in_range) * 100
        );
        recs.push({
          type: 'COMPLETION_WEAKNESS',
          scope_level: 'shop',
          shop_id: s.shop_id,
          shop_name: s.shop_name,
          title: `${s.shop_name} shows declining completion output`,
          summary: `Completion volume is down versus the previous period. Review whether this is caused by heavier job mix, bottlenecks, waiter pressure, or reduced technician availability.`,
          reasons: [
            `Completed ${s.completed_in_range} vs ${s.prior.completed_in_range} prior (${declinePct}% decline)`,
          ],
          priority_score: Math.min(100, 60 + declinePct),
          severity: 'medium',
        });
      }
    }

    if (s.active_tickets > 0 && s.waiterRatio > 0.4) {
      recs.push({
        type: 'WAITER_PRESSURE',
        scope_level: 'shop',
        shop_id: s.shop_id,
        shop_name: s.shop_name,
        title: `Waiter pressure is elevated at ${s.shop_name}`,
        summary: `This shop is carrying a high waiter mix relative to current load and capacity. Review front-counter scheduling discipline and same-day intake mix.`,
        reasons: [
          `${s.waiter_count} waiters of ${s.active_tickets} tickets (${Math.round(s.waiterRatio * 100)}%)`,
        ],
        priority_score: Math.round(s.waiterRatio * 100),
        severity: 'medium',
      });
    }
  });

  if (hasOverloaded && hasUnderutilized) {
    const maxObli = Math.max(...enriched.map((s) => s.obli.score));
    recs.push({
      type: 'CAPACITY_IMBALANCE',
      scope_level: 'scope',
      title: 'Capacity imbalance detected across your scope',
      summary: `At least one shop appears heavily loaded while another appears to have spare capacity. Review staffing, scheduling mix, and operational flow at the affected locations.`,
      reasons: [
        `${enriched.filter((s) => s.obli.score > 85 || s.utilization > 90).length} shop(s) overloaded`,
        `${enriched.filter((s) => s.obli.score < 20 && s.utilization < 30).length} shop(s) underutilized`,
      ],
      priority_score: maxObli,
      severity: 'high',
    });
  }

  recs.sort((a, b) => b.priority_score - a.priority_score);
  return recs;
}

// ── Severity UI helpers ────────────────────────────────

function getSeverityColor(severity: Recommendation['severity']) {
  switch (severity) {
    case 'high': return 'bg-destructive/20 text-destructive border-destructive/30';
    case 'medium': return 'bg-warning/20 text-warning border-warning/30';
    case 'low': return 'bg-blue-500/20 text-blue-600 border-blue-500/30';
  }
}

function getSeverityBadgeColor(severity: Recommendation['severity']) {
  switch (severity) {
    case 'high': return 'bg-destructive/20 text-destructive';
    case 'medium': return 'bg-warning/20 text-warning';
    case 'low': return 'bg-blue-500/20 text-blue-600';
  }
}

function getTypeIcon(type: RecommendationType) {
  switch (type) {
    case 'OVERLOAD_ATTENTION': return <AlertTriangle className="h-5 w-5" />;
    case 'UNDERUTILIZATION': return <TrendingDown className="h-5 w-5" />;
    case 'COMPLETION_WEAKNESS': return <TrendingDown className="h-5 w-5" />;
    case 'WAITER_PRESSURE': return <Clock className="h-5 w-5" />;
    case 'CAPACITY_IMBALANCE': return <Scale className="h-5 w-5" />;
  }
}

// ── Component ──────────────────────────────────────────

const OrgRecommendations = () => {
  const navigate = useNavigate();
  const { user, setActiveShop } = useAuth();
  const { canViewOrgDashboard, scopeLabel } = useEnterpriseScope();
  const { syncSignals } = useNetworkSignals();
  const {
    recordEvent,
    summary: learningSummary, isLoadingSummary, loadSummary,
    patterns, loadPatterns,
    recurrence, loadRecurrence,
    outcomes, loadOutcomes,
  } = useNetworkLearning();
  const pensieveLearning = useFeatureFlag('pensieveLearning');
  const { data: isAdmin } = useIsInternalAdmin();
  const showDebug = isAdmin || !import.meta.env.PROD;

  const [currentMetrics, setCurrentMetrics] = useState<DashboardMetrics | null>(null);
  const [priorMetrics, setPriorMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>('today');

  // Fetch data
  useEffect(() => {
    if (!user?.id || !canViewOrgDashboard) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const current = getDateRange(dateRange);
        const prior = getPriorDateRange(dateRange);

        const [currentRes, priorRes] = await Promise.all([
          supabase.rpc('get_org_dashboard_metrics', {
            _user_id: user.id,
            _start_date: current.start,
            _end_date: current.end,
          }),
          supabase.rpc('get_org_dashboard_metrics', {
            _user_id: user.id,
            _start_date: prior.start,
            _end_date: prior.end,
          }),
        ]);

        if (currentRes.error) console.error('[OrgRecommendations] Current RPC error:', currentRes.error);
        if (priorRes.error) console.error('[OrgRecommendations] Prior RPC error:', priorRes.error);

        setCurrentMetrics(currentRes.data as unknown as DashboardMetrics ?? null);
        setPriorMetrics(priorRes.data as unknown as DashboardMetrics ?? null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id, canViewOrgDashboard, dateRange]);

  // Generate recommendations
  const rawRecommendations = useMemo(() => {
    if (!currentMetrics?.per_shop) return [];
    return generateRecommendations(
      currentMetrics.per_shop,
      priorMetrics?.per_shop ?? [],
    );
  }, [currentMetrics, priorMetrics]);

  // Detect composite patterns (I-A-11)
  const compositePatterns = useMemo(() => {
    if (!pensieveLearning || rawRecommendations.length === 0) return [];
    return detectCompositePatterns(rawRecommendations, recurrence, patterns);
  }, [rawRecommendations, recurrence, patterns, pensieveLearning]);

  // Apply learning weights + pattern adjustments + outcome adjustments (bounded ±25 combined)
  const recommendations: EnrichedRecommendation[] = useMemo(() => {
    const withLearning = applyLearningToRecommendations(rawRecommendations, patterns, pensieveLearning);
    const withPatterns = applyPatternAdjustments(withLearning, compositePatterns);
    const withOutcomes = applyOutcomeAdjustments(withPatterns, outcomes, pensieveLearning);
    return withOutcomes.sort((a, b) => b.final_priority - a.final_priority) as EnrichedRecommendation[];
  }, [rawRecommendations, patterns, pensieveLearning, compositePatterns, outcomes]);

  // Sync recommendations to persistent signals (use raw, not learning-adjusted)
  useEffect(() => {
    if (rawRecommendations.length === 0 && !currentMetrics) return;
    const forSync = rawRecommendations.map((r) => ({
      signal_type: r.type,
      shop_id: r.shop_id,
      severity: r.severity,
      priority_score: r.priority_score,
      title: r.title,
      summary: r.summary,
      reasons: r.reasons,
    }));
    syncSignals(forSync);
  }, [rawRecommendations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Summary stats
  const summaryStats = useMemo(() => {
    const highCount = recommendations.filter((r) => r.severity === 'high').length;
    const attentionShops = new Set(
      recommendations.filter((r) => r.severity === 'high' && r.shop_id).map((r) => r.shop_id)
    ).size;
    const underutilizedCount = recommendations.filter((r) => r.type === 'UNDERUTILIZATION').length;
    const hasImbalance = recommendations.some((r) => r.type === 'CAPACITY_IMBALANCE');
    return { highCount, attentionShops, underutilizedCount, hasImbalance };
  }, [recommendations]);

  // Group by severity
  const grouped = useMemo(() => {
    const high = recommendations.filter((r) => r.severity === 'high');
    const medium = recommendations.filter((r) => r.severity === 'medium');
    const low = recommendations.filter((r) => r.severity === 'low');
    return { high, medium, low };
  }, [recommendations]);

  const handleViewShop = async (rec: Recommendation) => {
    if (rec.shop_id) {
      recordEvent('recommendation', null, 'recommendation_view_shop', rec.shop_id, { type: rec.type });
      await setActiveShop(rec.shop_id);
      navigate('/');
    }
  };

  const handleViewIntelligence = (rec: Recommendation) => {
    recordEvent('recommendation', null, 'recommendation_view_intelligence', rec.shop_id ?? null, { type: rec.type });
    navigate('/org/intelligence');
  };

  // Load Pensieve summary + patterns + recurrence + outcomes when recommendations are available
  useEffect(() => {
    if (recommendations.length > 0) {
      loadSummary(30);
      if (pensieveLearning) {
        loadPatterns(30);
        loadRecurrence(30);
        loadOutcomes(30);
      }
    }
  }, [recommendations.length, pensieveLearning]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive outcome insights
  const outcomeInsights = useMemo(() => {
    if (!pensieveLearning || outcomes.length === 0) return [];
    return deriveOutcomeInsights(outcomes);
  }, [outcomes, pensieveLearning]);

  // ── Guards ─────────────────────────────────────────

  if (!canViewOrgDashboard) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold">403 - Forbidden</h1>
          <p className="text-xl text-muted-foreground">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  const comparisonLabel = dateRange === 'today' ? 'vs Yesterday'
    : dateRange === 'yesterday' ? 'vs Day Before'
    : 'vs Prior 7 Days';

  // ── Render ─────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Stage 2 — back to OptiNetwork hub. */}
        <BackToOptiNetworkButton testId="org-recommendations-back-to-opti-network" />

        {/* ── Header ─────────────────────────────── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Lightbulb className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {scopeLabel} Recommendations
              </h1>
              <p className="text-sm text-muted-foreground">
                Prioritized operational guidance · {comparisonLabel}
              </p>
              {/* Phase 8 — deferral note. Backed by `get_org_dashboard_metrics`,
                  which does not accept a scope filter. */}
              <p className="text-xs italic text-muted-foreground mt-1">
                Showing organization-level recommendations. Region- and
                district-scope filtering is deferred to a future phase.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/org')}>
              ← Dashboard
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/org/intelligence')}>
              Intelligence →
            </Button>
            <div className="flex gap-1">
              {([
                ['today', 'Today'],
                ['yesterday', 'Yesterday'],
                ['7d', 'Last 7 Days'],
              ] as [DateRange, string][]).map(([key, label]) => (
                <Button
                  key={key}
                  size="sm"
                  variant={dateRange === key ? 'default' : 'outline'}
                  onClick={() => setDateRange(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-muted-foreground">Generating recommendations…</div>
          </div>
        ) : !currentMetrics ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-muted-foreground">Failed to load data</div>
          </div>
        ) : (
          <>
            {/* ── Summary Strip ──────────────────── */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatCard
                title="High Priority"
                value={summaryStats.highCount}
                icon={AlertTriangle}
              />
              <StatCard
                title="Shops Needing Attention"
                value={summaryStats.attentionShops}
                icon={Eye}
              />
              <StatCard
                title="Underutilized Shops"
                value={summaryStats.underutilizedCount}
                icon={TrendingDown}
              />
              <StatCard
                title="Capacity Imbalance"
                value={summaryStats.hasImbalance ? 'Yes' : 'No'}
                icon={Scale}
              />
            </div>

            {/* ── Recommendation Feed ──────────────── */}
            {recommendations.length === 0 ? (
              <Card>
                <CardContent className="flex items-center gap-3 p-6">
                  <CheckCircle className="h-6 w-6 text-success" />
                  <div>
                    <p className="font-medium text-foreground">No major recommendations detected</p>
                    <p className="text-sm text-muted-foreground">
                      Your current scope appears balanced based on the configured signals.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {grouped.high.length > 0 && (
                  <RecommendationGroup
                    title="High Priority"
                    items={grouped.high}
                    onViewShop={handleViewShop}
                    onViewIntelligence={handleViewIntelligence}
                    showDebug={showDebug && pensieveLearning}
                    recurrence={recurrence}
                    recordEvent={recordEvent}
                    outcomes={outcomes}
                  />
                )}

                {grouped.medium.length > 0 && (
                  <RecommendationGroup
                    title="Watchlist"
                    items={grouped.medium}
                    onViewShop={handleViewShop}
                    onViewIntelligence={handleViewIntelligence}
                    showDebug={showDebug && pensieveLearning}
                    recurrence={recurrence}
                    recordEvent={recordEvent}
                    outcomes={outcomes}
                  />
                )}

                {grouped.low.length > 0 && (
                  <RecommendationGroup
                    title="Opportunity"
                    items={grouped.low}
                    onViewShop={handleViewShop}
                    onViewIntelligence={handleViewIntelligence}
                    showDebug={showDebug && pensieveLearning}
                    recurrence={recurrence}
                    recordEvent={recordEvent}
                    outcomes={outcomes}
                  />
                )}
              </div>
            )}

            {/* ── Emerging Patterns (I-A-11) ─────────── */}
            {pensieveLearning && compositePatterns.length > 0 && (
              <EmergingPatterns patterns={compositePatterns} />
            )}

            {/* ── Pensieve Insights ──────────────── */}
            <PensieveInsights summary={learningSummary} isLoading={isLoadingSummary} outcomeInsights={outcomeInsights} />
          </>
        )}
      </div>
    </div>
  );
};

// ── Recommendation Group ───────────────────────────────

function RecommendationGroup({
  title,
  items,
  onViewShop,
  onViewIntelligence,
  showDebug = false,
  recurrence = [],
  recordEvent,
  outcomes = [],
}: {
  title: string;
  items: EnrichedRecommendation[];
  onViewShop: (rec: Recommendation) => void;
  onViewIntelligence: (rec: Recommendation) => void;
  showDebug?: boolean;
  recurrence?: { signal_type: string; shop_id: string; occurrence_count: number }[];
  recordEvent: (source: any, sourceId: string | null, eventType: any, shopId?: string | null, metadata?: Record<string, unknown>) => void;
  outcomes?: OutcomePattern[];
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [activePlaybook, setActivePlaybook] = useState<{ playbook: Playbook; rec: EnrichedRecommendation } | null>(null);
  const [checkedSteps, setCheckedSteps] = useState<Set<string>>(new Set());

  const handleOpenPlaybook = (rec: EnrichedRecommendation) => {
    const pb = getPlaybook(rec);
    if (!pb) return;
    setActivePlaybook({ playbook: pb, rec });
    setCheckedSteps(new Set());
    recordEvent('recommendation', null, 'playbook_opened', rec.shop_id ?? null, {
      type: rec.type,
      playbook_type: pb.playbook_type,
    });
  };

  const handleStepCheck = (stepId: string) => {
    setCheckedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
        recordEvent('recommendation', null, 'playbook_step_viewed', activePlaybook?.rec.shop_id ?? null, {
          playbook_type: activePlaybook?.playbook.playbook_type,
          step_id: stepId,
        });
      }
      return next;
    });
  };

  const handleStepNavigate = (step: PlaybookStep) => {
    if (!activePlaybook) return;
    recordEvent('recommendation', null, 'playbook_step_clicked', activePlaybook.rec.shop_id ?? null, {
      playbook_type: activePlaybook.playbook.playbook_type,
      step_id: step.step_id,
      route_type: step.route_type,
    });
    if (step.route_type === 'shop') {
      onViewShop(activePlaybook.rec);
    } else if (step.route_type === 'intelligence') {
      onViewIntelligence(activePlaybook.rec);
    }
  };

  const handleActionClick = (rec: EnrichedRecommendation, action: GuidedAction) => {
    recordEvent('recommendation', null, 'guided_action_clicked', rec.shop_id ?? null, {
      type: rec.type,
      action_type: action.action_type,
    });
    if (action.route_type === 'shop' && action.target_shop_id) {
      onViewShop(rec);
    } else {
      onViewIntelligence(rec);
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {items.map((rec, i) => {
        const hasLearning = rec.learning_adjustment !== 0 && rec.learning_reasons.length > 0;
        const hasPattern = rec.pattern_adjustment !== 0 && rec.pattern_reasons.length > 0;
        const hasOutcome = rec.outcome_adjustment !== 0 && rec.outcome_reasons.length > 0;
        const hasAnyAdjustment = hasLearning || hasPattern || hasOutcome;
        const isExpanded = expandedIdx === i;
        const guidedActions = generateGuidedActions(rec);

        // Check if this rec's shop has recurrence
        const shopRecurrence = rec.shop_id
          ? recurrence.find((r) => r.shop_id === rec.shop_id && r.signal_type === rec.type)
          : null;

        return (
          <Card key={`${rec.type}-${rec.shop_id ?? 'scope'}-${i}`} className={cn('border', getSeverityColor(rec.severity).split(' ')[2])}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className={cn('mt-0.5', getSeverityColor(rec.severity).split(' ')[1])}>
                    {getTypeIcon(rec.type)}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground">{rec.title}</h3>
                      <Badge className={cn(getSeverityBadgeColor(rec.severity), 'border-0 text-xs')}>
                        {rec.severity === 'high' ? 'High' : rec.severity === 'medium' ? 'Medium' : 'Low'}
                      </Badge>
                      {hasLearning && (
                        <Badge variant="outline" className="text-xs gap-1 border-primary/30 text-primary">
                          <Brain className="h-3 w-3" />
                          Learning
                        </Badge>
                      )}
                      {rec.confidence && (
                        <Badge variant="outline" className={cn('text-xs gap-1', getConfidenceBadgeClass(rec.confidence))}>
                          <Target className="h-3 w-3" />
                          {rec.confidence === 'high' ? 'High Confidence' : rec.confidence === 'medium' ? 'Medium Confidence' : 'Low Confidence'}
                        </Badge>
                      )}
                      {shopRecurrence && shopRecurrence.occurrence_count >= 3 && (
                        <Badge variant="outline" className="text-xs gap-1 border-warning/30 text-warning">
                          <RefreshCw className="h-3 w-3" />
                          Recurring Issue
                        </Badge>
                      )}
                      {(() => {
                        const outcomeBadge = getOutcomeBadge(rec.type, outcomes);
                        return outcomeBadge ? (
                          <Badge variant="outline" className={cn('text-xs gap-1', outcomeBadge.className)}>
                            <Eye className="h-3 w-3" />
                            {outcomeBadge.label}
                          </Badge>
                        ) : null;
                      })()}
                      {hasOutcome && (
                        <Badge variant="outline" className="text-xs gap-1 border-primary/20 text-primary/80">
                          <Activity className="h-3 w-3" />
                          Outcome-Refined
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{rec.summary}</p>
                  </div>
                </div>
              </div>

              {/* Reasons */}
              <div className="ml-8 space-y-1">
                {rec.reasons.map((reason, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm text-foreground">
                    <Activity className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span>{reason}</span>
                  </div>
                ))}
              </div>

              {/* Learning + Pattern explainability */}
              {hasAnyAdjustment && (
                <div className="ml-8">
                  <button
                    onClick={() => {
                      const willExpand = !isExpanded;
                      setExpandedIdx(willExpand ? i : null);
                      if (willExpand) {
                        recordEvent('recommendation', null, 'guided_action_viewed', rec.shop_id ?? null, { type: rec.type });
                      }
                    }}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    Why prioritized
                  </button>
                  {isExpanded && (
                    <div className="mt-1.5 space-y-1 pl-4 border-l-2 border-primary/20">
                      {hasLearning && rec.learning_reasons.map((reason, j) => (
                        <p key={`l-${j}`} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Brain className="h-3 w-3 text-primary/60 shrink-0" />
                          {reason}
                        </p>
                      ))}
                      {hasPattern && rec.pattern_reasons.map((reason, j) => (
                        <p key={`p-${j}`} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Target className="h-3 w-3 text-warning/60 shrink-0" />
                          {reason}
                        </p>
                      ))}
                      {hasOutcome && rec.outcome_reasons.map((reason, j) => (
                        <p key={`o-${j}`} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Activity className="h-3 w-3 text-primary/40 shrink-0" />
                          Refined by outcome awareness: {reason}
                        </p>
                      ))}
                      {(() => {
                        const ctx = getOutcomeContext(rec.type, outcomes);
                        return ctx ? (
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Eye className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                            {ctx}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Debug line (internal/dev only) */}
              {showDebug && hasAnyAdjustment && (
                <div className="ml-8 text-[10px] font-mono text-muted-foreground">
                  base: {rec.base_priority} | learn: {rec.learning_adjustment > 0 ? '+' : ''}{rec.learning_adjustment} | pattern: {rec.pattern_adjustment > 0 ? '+' : ''}{rec.pattern_adjustment} | outcome: {rec.outcome_adjustment > 0 ? '+' : ''}{rec.outcome_adjustment} | final: {rec.final_priority}
                </div>
              )}

              {/* Suggested Actions (I-A-12) */}
              {guidedActions.length > 0 && (
                <div className="ml-8 space-y-2">
                  <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Compass className="h-3.5 w-3.5" />
                      Suggested Actions
                    </div>
                    {getPlaybook(rec) && (
                      <button
                        onClick={() => handleOpenPlaybook(rec)}
                        className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                      >
                        <BookOpen className="h-3.5 w-3.5" />
                        Open Playbook
                      </button>
                    )}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {guidedActions.map((action, ai) => (
                      <div
                        key={ai}
                        className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2"
                      >
                        <p className="text-sm font-medium text-foreground">{action.title}</p>
                        <p className="text-xs text-muted-foreground">{action.description}</p>
                        <p className="text-xs text-muted-foreground/70 italic">{action.context_hint}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleActionClick(rec, action)}
                          className="gap-1 w-full"
                        >
                          {action.route_type === 'shop' ? (
                            <Store className="h-3.5 w-3.5" />
                          ) : (
                            <Zap className="h-3.5 w-3.5" />
                          )}
                          {action.cta_label}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      {/* Playbook Drawer */}
      <Drawer open={!!activePlaybook} onOpenChange={(open) => { if (!open) setActivePlaybook(null); }}>
        <DrawerContent className="max-h-[85vh]">
          {activePlaybook && (
            <>
              <DrawerHeader>
                <DrawerTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  {activePlaybook.playbook.title}
                </DrawerTitle>
                <DrawerDescription>
                  {activePlaybook.playbook.summary}
                </DrawerDescription>
              </DrawerHeader>
              <div className="px-4 pb-6 space-y-3 overflow-y-auto">
                {activePlaybook.playbook.steps.map((step, idx) => (
                  <div
                    key={step.step_id}
                    className={cn(
                      'rounded-md border p-3 space-y-2 transition-colors',
                      checkedSteps.has(step.step_id) ? 'bg-muted/50 border-primary/20' : 'border-border/60'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={checkedSteps.has(step.step_id)}
                        onCheckedChange={() => handleStepCheck(step.step_id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 space-y-1">
                        <p className={cn(
                          'text-sm font-medium',
                          checkedSteps.has(step.step_id) ? 'text-muted-foreground line-through' : 'text-foreground'
                        )}>
                          Step {idx + 1}: {step.title}
                        </p>
                        <p className="text-xs text-muted-foreground">{step.description}</p>
                        <p className="text-xs text-muted-foreground/70 italic">{step.context_hint}</p>
                      </div>
                    </div>
                    {step.route_type !== 'none' && (
                      <div className="ml-7">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStepNavigate(step)}
                          className="gap-1"
                        >
                          {step.route_type === 'shop' ? (
                            <Store className="h-3.5 w-3.5" />
                          ) : (
                            <Zap className="h-3.5 w-3.5" />
                          )}
                          {step.route_type === 'shop' ? 'Open Shop' : 'View Intelligence'}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}

// ── Emerging Patterns (I-A-11) ─────────────────────────

function EmergingPatterns({ patterns }: { patterns: CompositePattern[] }) {
  // Show top 3
  const topPatterns = patterns.slice(0, 3);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Target className="h-5 w-5 text-warning" />
          Emerging Patterns
          <Badge variant="outline" className="text-xs font-normal">Adaptive Intelligence</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {topPatterns.map((pattern, i) => (
          <div key={i} className="rounded-md border p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-foreground">
                {getPatternBadgeLabel(pattern)}
              </span>
              <Badge variant="outline" className={cn('text-xs', getConfidenceBadgeClass(pattern.confidence))}>
                {pattern.confidence === 'high' ? 'High Confidence' : pattern.confidence === 'medium' ? 'Medium' : 'Low'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{pattern.explanation}</p>
            {pattern.context_lines.map((line, j) => (
              <p key={j} className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Activity className="h-3 w-3 text-muted-foreground shrink-0" />
                {line}
              </p>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Pensieve Insights ──────────────────────────────────

function PensieveInsights({
  summary,
  isLoading,
  outcomeInsights = [],
}: {
  summary: LearningSummaryRow[];
  isLoading: boolean;
  outcomeInsights?: OutcomeInsight[];
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Loading Pensieve insights…
        </CardContent>
      </Card>
    );
  }

  if (summary.length === 0) return null;

  const signalAcked = summary.find((s) => s.event_type === 'signal_acknowledged');
  const signalDismissed = summary.find((s) => s.event_type === 'signal_dismissed');
  const recViewShop = summary.find((s) => s.event_type === 'recommendation_view_shop');
  const resolvedAfterAck = summary.find((s) => s.event_type === 'signal_resolved_after_acknowledgement');

  const hasData = signalAcked || signalDismissed || recViewShop || resolvedAfterAck;
  if (!hasData) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          Pensieve Insights
          <Badge variant="outline" className="text-xs font-normal">Last 30 days</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {signalAcked && (
            <div className="rounded-md border p-3 space-y-1">
              <p className="text-xs text-muted-foreground">Signals Acknowledged</p>
              <p className="text-2xl font-bold text-foreground">{signalAcked.event_count}</p>
            </div>
          )}
          {signalDismissed && (
            <div className="rounded-md border p-3 space-y-1">
              <p className="text-xs text-muted-foreground">Signals Dismissed</p>
              <p className="text-2xl font-bold text-foreground">{signalDismissed.event_count}</p>
            </div>
          )}
          {recViewShop && (
            <div className="rounded-md border p-3 space-y-1">
              <p className="text-xs text-muted-foreground">Shop Drilldowns</p>
              <p className="text-2xl font-bold text-foreground">{recViewShop.event_count}</p>
            </div>
          )}
          {resolvedAfterAck && (
            <div className="rounded-md border p-3 space-y-1">
              <p className="text-xs text-muted-foreground">Resolved After Ack</p>
              <p className="text-2xl font-bold text-foreground">{resolvedAfterAck.event_count}</p>
            </div>
          )}
        </div>
        {/* Outcome Trends sub-section */}
        {outcomeInsights.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <Eye className="h-4 w-4 text-muted-foreground" />
              Outcome Trends
            </h4>
            <div className="grid gap-2 sm:grid-cols-2">
              {outcomeInsights.slice(0, 4).map((insight) => (
                <div key={insight.id} className="rounded-md border p-3 space-y-1">
                  <p className="text-xs font-medium text-foreground">{insight.label}</p>
                  <p className="text-xs text-muted-foreground">{insight.detail}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default OrgRecommendations;
